import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../lib/auth';
import { MenuStack } from './MenuStack';
import { MenuWheel } from './MenuWheel';
import { getMenuStyle, setMenuStyle, type MenuStyle } from '../../lib/preferences';
import { haptic } from '../../lib/haptic';
import { env } from '../../lib/env';

type Props = { open: boolean; onClose: () => void };

export function MenuDrawer({ open, onClose }: Props) {
  const { t } = useTranslation('menu');
  const driver = useAuth(s => s.driver);
  const [style, setStyleState] = useState<MenuStyle>(() => getMenuStyle());

  if (!open) return null;

  const setStyle = (next: MenuStyle) => {
    if (next === style) return;
    haptic.tap();
    setStyleState(next);
    setMenuStyle(next);
  };

  const initials = (driver?.name || 'D').trim().split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase();
  // Photo via the public /v1/drivers/:id/photo endpoint when available.
  const apiBase = env().VITE_API_BASE.replace(/\/+$/, '');
  const photoSrc = driver?.hasPhoto && driver?.id ? `${apiBase}/v1/drivers/${driver.id}/photo` : null;

  return (
    <>
      {/* Scrim (tap to close) */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.55)',
          zIndex: 20,
        }}
        aria-hidden="true"
      />
      {/* Menu surface — dark green "command center". Constrained to the
          440px column on desktop (the scrim above still covers the full
          viewport so taps outside the column close the menu). On mobile
          this is functionally identical to inset:0 since the column
          fills the viewport. */}
      <div
        role="dialog"
        aria-label={t('home')}
        style={{
          position: 'fixed',
          top: 0,
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100%',
          maxWidth: 440,
          zIndex: 30,
          display: 'flex',
          flexDirection: 'column',
          padding: '18px 18px calc(18px + env(safe-area-inset-bottom))',
          background:
            'radial-gradient(circle at 18% 25%, rgba(46, 158, 101, 0.30), transparent 55%), ' +
            'radial-gradient(circle at 82% 75%, rgba(31, 110, 71, 0.45), transparent 60%), ' +
            'linear-gradient(140deg, rgb(15, 19, 16), rgb(15, 59, 38) 60%, rgb(24, 84, 55))',
          color: 'rgba(255, 255, 255, 0.92)',
          overflowY: 'auto',
        }}
      >
        {/* Top bar — style toggle (left) + close (right) */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '8px',
          }}
        >
          <div
            role="tablist"
            aria-label="Menu style"
            style={{
              display: 'flex',
              background: 'rgba(255, 255, 255, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.14)',
              borderRadius: '9999px',
              padding: '3px',
              gap: '2px',
            }}
          >
            <StyleToggleButton
              active={style === 'wheel'}
              label="Wheel layout"
              onClick={() => setStyle('wheel')}
              icon={<WheelIcon />}
            />
            <StyleToggleButton
              active={style === 'stack'}
              label="Stack layout"
              onClick={() => setStyle('stack')}
              icon={<StackIcon />}
            />
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '999px',
              border: '1px solid rgba(255, 255, 255, 0.18)',
              background: 'rgba(255, 255, 255, 0.10)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.22)',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Profile mini-block */}
        <div style={{ textAlign: 'center', marginBottom: '12px' }}>
          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '999px',
              background: 'var(--mint-soft)',
              color: 'var(--green-700)',
              margin: '0 auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '26px',
              fontWeight: 800,
              letterSpacing: '-0.02em',
              border: '3px solid rgba(255, 255, 255, 0.20)',
              boxShadow: '0 4px 18px rgba(46, 158, 101, 0.35)',
              overflow: 'hidden',
              position: 'relative',
            }}
            aria-hidden="true"
          >
            {photoSrc ? (
              <img
                src={photoSrc}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
            ) : null}
            {!photoSrc ? (initials || 'D') : (
              <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: -1 }}>{initials || 'D'}</span>
            )}
          </div>
          {driver?.name && (
            <div
              style={{
                fontSize: '18px',
                fontWeight: 800,
                letterSpacing: '-0.025em',
                marginTop: '10px',
                color: 'white',
              }}
            >
              {driver.name}
            </div>
          )}
        </div>

        {/* Mode content */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {style === 'wheel' ? <MenuWheel onClose={onClose} /> : <MenuStack onClose={onClose} />}
        </div>
      </div>
    </>
  );
}

function StyleToggleButton({
  active,
  label,
  icon,
  onClick,
}: {
  active: boolean;
  label: string;
  icon: JSX.Element;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      aria-label={label}
      title={label}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '44px',
        height: '44px',
        padding: 0,
        border: 0,
        background: active ? 'white' : 'transparent',
        borderRadius: '9999px',
        color: active ? '#1A1A1A' : 'rgba(255, 255, 255, 0.65)',
        cursor: 'pointer',
        transition: 'background 140ms ease, color 140ms ease, box-shadow 140ms ease',
        boxShadow: active ? '0 2px 6px rgba(0, 0, 0, 0.18)' : 'none',
        fontWeight: active ? 700 : 400,
      }}
    >
      <span style={{ width: '13px', height: '13px', display: 'block' }}>{icon}</span>
    </button>
  );
}

function WheelIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="2" x2="12" y2="22" />
      <line x1="2" y1="12" x2="22" y2="12" />
    </svg>
  );
}

function StackIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}
